import {
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiOperator,
  ApiYAxisChartType,
} from "@incendium/api";
import { Grid, Stack, capitalize } from "@mui/material";
import { StatCardTypography } from "Components/UI/StatCard";
import { useAudienceAnalyticsContext } from "Providers/AudienceAnalyticsProvider";
import { useMemo } from "react";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import { AnalyticsStatCard } from "features/analytics";
import {
  CHART_KEY_AUDIENCE_NEW_RETURNING_AVG_TIME_ON_PAGE_TREND,
  CHART_KEY_AUDIENCE_NEW_RETURNING_EFFECTIVE_SESSION_TREND,
  CHART_KEY_AUDIENCE_NEW_RETURNING_SESSION_CONVERSIONS,
  CHART_KEY_AUDIENCE_NEW_RETURNING_SESSIONS_BY_CHANNELS,
  CHART_KEY_AUDIENCE_NEW_RETURNING_SESSIONS_BY_CHANNELS_COMPOSITION,
  CHART_KEY_AUDIENCE_NEW_RETURNING_TOP_LANDING_PAGES,
  CHART_KEY_AUDIENCE_NEW_RETURNING_TOP_MARKETING_CHANNELS,
  CHART_KEY_AUDIENCE_NEW_RETURNING_TOP_SECOND_PAGEVIEWS,
} from "features/analytics/constants/chartKeys";

interface IAudienceNewAndReturningReportPageProps {
  userType: "New" | "Returning";
  isCustomer?: boolean;
}

function AudienceNewAndReturningReportPage({
  userType,
  isCustomer = false,
}: IAudienceNewAndReturningReportPageProps) {
  const { currentAudience } = useAudienceAnalyticsContext();

  const attributes = useMemo(() => {
    const filters: ApiChartAttribute[] = [
      {
        key: isCustomer
          ? ApiDimension.DIMENSION_CUSTOMER_TYPE
          : ApiDimension.DIMENSION_VISITOR_TYPE,
        operator: ApiOperator.CONTAINS,
        value: userType,
      },
    ];
    if (currentAudience && currentAudience !== "") {
      filters.push({
        key: ApiDimension.DIMENSION_LEAD_AUDIENCE,
        value: currentAudience,
      });
    }
    return filters;
  }, [userType, currentAudience, isCustomer]);

  // if (!false) return <>hmm</>

  return (
    <Grid container spacing={2} mb={2}>
      <Grid item xs={3}>
        <Stack spacing={2}>
          <AnalyticsStatCard
            chart={{
              name: "",
              dimension: [ApiDimension.DIMENSION_CHANNEL],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.GRAPH,
              attributes,
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                },
              ],
              limit: 1,
            }}
            renderBody={(data) => (
              <>
                <StatCardTypography size="small" mb={1}>
                  Most Sessions by Marketing Channel
                </StatCardTypography>
                <StatCardTypography size="medium">
                  {data[0]?.name}
                </StatCardTypography>
              </>
            )}
          />
          <AnalyticsStatCard
            chart={{
              name: "",
              dimension: [ApiDimension.DIMENSION_CHANNEL],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.GRAPH,
              attributes,
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_EFFECTIVE_SESSION_RATE],
                },
              ],
              limit: 10,
            }}
            renderBody={(data) => (
              <>
                <StatCardTypography size="small" mb={1}>
                  Most Engaging Marketing Channel
                </StatCardTypography>
                <StatCardTypography size="medium">
                  {data[0]?.name}
                </StatCardTypography>
              </>
            )}
          />
          <AnalyticsStatCard
            minHeight={110}
            chart={{
              name: "",
              dimension: [ApiDimension.DIMENSION_DEVICE],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.GRAPH,
              attributes,
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_LEADS_COUNT],
                },
              ],
              limit: 1,
            }}
            renderBody={(data) => (
              <>
                <StatCardTypography size="small" mb={1}>
                  Most Used Device
                </StatCardTypography>
                <StatCardTypography size="medium">
                  {capitalize(data[0]?.name || "")}
                </StatCardTypography>
              </>
            )}
          />
        </Stack>
      </Grid>
      <Grid item xs={6}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_AUDIENCE_NEW_RETURNING_SESSIONS_BY_CHANNELS}
          chart={{
            name: `${userType} Sessions by Channels`,
            dimension: [ApiDimension.DIMENSION_CHANNEL],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.GRAPH,
            attributes,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                chart: {
                  [ApiMetric.METRIC_SESSIONS_COUNT]: ApiYAxisChartType.BAR,
                },
              },
              {
                key: "r",
                fields: [ApiMetric.METRIC_MACRO_CONVERSION_RATE],
                chart: {
                  [ApiMetric.METRIC_MACRO_CONVERSION_RATE]:
                    ApiYAxisChartType.DOT,
                },
              },
            ],
            displayOptions: {
              noFilters: true,
            },
          }}
        />
      </Grid>
      <Grid item xs={3}>
        <StandardAnalyticsCard
          chartKey={
            CHART_KEY_AUDIENCE_NEW_RETURNING_SESSIONS_BY_CHANNELS_COMPOSITION
          }
          chart={{
            name: `${userType} Sessions by Channels Composition`,
            dimension: [ApiDimension.DIMENSION_CHANNEL],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.PIE,
            attributes,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSIONS_COUNT],
              },
            ],
            displayOptions: {
              noFilters: true,
              showTotals: true,
            },
          }}
        />
      </Grid>
      <Grid item xs={6} sx={{ minHeight: 470 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_AUDIENCE_NEW_RETURNING_TOP_LANDING_PAGES}
          chart={{
            name: `${userType} Top Landing Pages`,
            dimension: [ApiDimension.DIMENSION_LANDING_PAGE_LOCATION],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes,
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_SESSIONS_COUNT,
                  ApiMetric.METRIC_LEADS_COUNT,
                  ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                  ApiMetric.METRIC_AVERAGE_PAGEVIEWS_PER_SESSION,
                  ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
                  ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT,
                  ApiMetric.METRIC_MACRO_CONVERSION_RATE,
                ],
              },
            ],
            displayOptions: {
              noFilters: true,
            },
          }}
        />
      </Grid>
      <Grid item xs={6} sx={{ minHeight: 470 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_AUDIENCE_NEW_RETURNING_TOP_SECOND_PAGEVIEWS}
          chart={{
            name: `${userType} Top Second Pageviews`,
            dimension: [ApiDimension.DIMENSION_LANDING_NEXT_PAGE_LOCATION],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.GRAPH,
            attributes,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_LEADS_COUNT],
                chart: {
                  [ApiMetric.METRIC_LEADS_COUNT]: ApiYAxisChartType.BAR,
                },
              },
            ],
            displayOptions: {
              noFilters: true,
              noLegend: true,
              filterDropdowns: [ApiDimension.DIMENSION_SESSION_LANDING_PAGE_LOCATION],
            },
          }}
        />
      </Grid>
      <Grid item xs={6} sx={{ minHeight: 415 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_AUDIENCE_NEW_RETURNING_TOP_MARKETING_CHANNELS}
          chart={{
            name: `Top 5 Marketing Channels for ${userType} `,
            dimension: [
              ApiDimension.DIMENSION_SESSION_START_BY_DAY,
              ApiDimension.DIMENSION_CHANNEL,
            ],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            attributes,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSIONS_COUNT],
              },
            ],
            displayOptions: {
              noFilters: true,
            },
          }}
        />
      </Grid>

      <Grid item xs={6} sx={{ minHeight: 415 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_AUDIENCE_NEW_RETURNING_EFFECTIVE_SESSION_TREND}
          chart={{
            name: `${userType} Effective Session Trend`,
            dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            attributes,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT],
                chart: {
                  [ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT]:
                    ApiYAxisChartType.AREA,
                },
              },
            ],
            displayOptions: {
              noFilters: true,
              noLegend: true,
            },
          }}
        />
      </Grid>
      <Grid item xs={6} sx={{ minHeight: 415 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_AUDIENCE_NEW_RETURNING_AVG_TIME_ON_PAGE_TREND}
          chart={{
            name: `${userType} Avg. Time Spend on Page Trend`,
            dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            attributes,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE],
              },
            ],
            displayOptions: {
              noFilters: true,
              noLegend: true,
            },
          }}
        />
      </Grid>

      <Grid item xs={6} sx={{ minHeight: 415 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_AUDIENCE_NEW_RETURNING_SESSION_CONVERSIONS}
          chart={{
            name: `${userType} Session Conversions`,
            dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            attributes,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT],
                chart: {
                  [ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT]:
                    ApiYAxisChartType.AREA,
                },
              },
            ],
            displayOptions: {
              noFilters: true,
              noLegend: true,
            },
          }}
        />
      </Grid>
    </Grid>
  );
}

export default AudienceNewAndReturningReportPage;
